import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';

const NOW = new Date();
const DEFAULT = {
  periodYear: NOW.getUTCFullYear(),
  periodMonth: NOW.getUTCMonth() + 1, // 1..12
};

export function PayrollRunModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(DEFAULT);
  const [error, setError] = useState(null);

  const create = useMutation({
    mutationFn: (body) => hrApi.createPayrollRun(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'payroll'] });
      setForm(DEFAULT);
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const handleNumber = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: Number(e.target.value) }));

  function submit(e) {
    e.preventDefault();
    setError(null);
    create.mutate({
      periodYear: form.periodYear,
      periodMonth: form.periodMonth,
    });
  }

  const years = [];
  for (let y = NOW.getUTCFullYear() - 2; y <= NOW.getUTCFullYear() + 1; y += 1) {
    years.push(y);
  }
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('hr.payroll.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p className="muted text-sm" style={{ margin: 0 }}>
          {t('hr.payroll.modal.note')}
        </p>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('hr.payroll.modal.year')}</span>
            <select className="select" value={form.periodYear} onChange={handleNumber('periodYear')}>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('hr.payroll.modal.month')}</span>
            <select className="select" value={form.periodMonth} onChange={handleNumber('periodMonth')}>
              {months.map((m) => (
                <option key={m} value={m}>{t(`month.${m}`)}</option>
              ))}
            </select>
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
